import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import { del, get } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/format";
import { qs } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ErrorState, ListCard, SearchInput, TableSkeleton } from "@/components/common/DataStates";
import { CompanyDialog } from "@/components/crm/dialogs";
import { RowMenu, rowProps } from "@/pages/crm/shared";

const PAGE_SIZE = 25;

export const CompaniesTab = ({ onOpenRecord, openNew, onNewHandled }) => {
  const { t } = useTranslation();
  const { orgId, hasRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasRole("member");
  const isAdmin = hasRole("admin");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const params = qs({ q, page, page_size: PAGE_SIZE });
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ["crm-companies", orgId, params], queryFn: () => get(`/crm/companies${params}`), enabled: !!orgId, placeholderData: (prev) => prev });
  const remove = useMutation({ mutationFn: (id) => del(`/crm/companies/${id}`), onSuccess: () => { toast.success(t("common.deleted")); setToDelete(null); qc.invalidateQueries({ queryKey: ["crm-companies"] }); }, onError: (e) => toast.error(errorMessage(e)) });
  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="crm-companies-tab">
      <ListCard
        refreshing={isFetching && !isLoading}
        testId="crm-companies-card"
        toolbar={
          <>
            <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} className="sm:w-72" testId="crm-companies-search" />
            {canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-companies-new"><Plus className="mr-2 h-4 w-4" />{t("crm.companies.new")}</Button>}
          </>
        }
        footer={data && <Pagination page={data.page} pageSize={data.page_size} total={data.total} onChange={setPage} testId="crm-companies-pagination" />}
      >
        {isError && <div className="p-4"><ErrorState message={errorMessage(error)} onRetry={refetch} testId="crm-companies-error" /></div>}
        {isLoading && <TableSkeleton testId="crm-companies-loading" />}
        {!isLoading && !isError && items.length === 0 && (
          <div className="p-4"><EmptyState icon={Building2} title={q ? t("common.noResults") : t("crm.companies.empty")} hint={q ? undefined : t("crm.companies.emptyHint")} className="border-dashed shadow-none" testId="crm-companies-empty" action={!q && canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-companies-empty-new"><Plus className="mr-2 h-4 w-4" />{t("crm.companies.new")}</Button>} /></div>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <Table data-testid="crm-companies-table">
              <TableHeader className="og-table-head"><TableRow><TableHead>{t("crm.companies.name")}</TableHead><TableHead className="hidden md:table-cell">{t("crm.companies.industry")}</TableHead><TableHead className="hidden lg:table-cell">{t("crm.companies.city")}</TableHead><TableHead className="hidden text-right md:table-cell">{t("crm.companies.contacts")}</TableHead><TableHead className="hidden text-right xl:table-cell">{t("crm.record.leads")} / {t("crm.record.opportunities")}</TableHead><TableHead className="hidden md:table-cell">{t("common.created")}</TableHead><TableHead className="w-10" /></TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} {...rowProps(() => onOpenRecord("company", c.id), `crm-company-row-${c.id}`)}>
                    <TableCell>
                      <div className="font-medium" data-testid={`crm-company-name-${c.id}`}>{c.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{c.website || c.email || "—"}</div>
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">{c.industry || "—"}</TableCell>
                    <TableCell className="hidden text-sm lg:table-cell">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</TableCell>
                    <TableCell className="hidden text-right font-mono text-xs md:table-cell">{c.contacts_count ?? 0}</TableCell>
                    <TableCell className="hidden text-right font-mono text-xs xl:table-cell">{c.leads_count ?? 0} / {c.opportunities_count ?? 0}</TableCell>
                    <TableCell className="hidden whitespace-nowrap text-xs text-muted-foreground md:table-cell">{formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <RowMenu testId={`crm-company-menu-${c.id}`} items={[
                        { key: "open", label: t("common.open"), onSelect: () => onOpenRecord("company", c.id) },
                        { key: "edit", label: t("common.edit"), onSelect: () => setEditing(c), hidden: !canEdit },
                        { key: "delete", label: t("common.delete"), onSelect: () => setToDelete(c), destructive: true, separator: true, hidden: !isAdmin },
                      ]} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ListCard>
      <CompanyDialog open={!!editing || !!openNew} onOpenChange={(o) => { if (!o) { setEditing(null); onNewHandled?.(); } }} initial={editing?.id ? editing : null} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} description={t("common.confirmDelete")} destructive loading={remove.isPending} confirmLabel={t("common.delete")} onConfirm={() => remove.mutate(toDelete.id)} testId="crm-company-delete-confirm" />
    </div>
  );
};
